/**
 * Error Groups Store
 * Zustand store for managing error groups state
 */
import { create } from 'zustand';
import errorGroupsService from '@/services/errorGroupsService';
import type {
  ErrorGroup,
  ErrorGroupListParams,
  ReprocessRequest
} from '@/services/errorGroupsService';
import { showAlert } from '@/stores/alertStore';

interface ErrorGroupsState {
  errorGroups: ErrorGroup[];
  totalCount: number;
  loading: boolean;
  currentPage: number;
  pageSize: number;

  // Actions
  getErrorGroups: (params?: ErrorGroupListParams) => Promise<void>;
  reprocessErrorGroups: (request: ReprocessRequest) => Promise<boolean>;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export const useErrorGroupsStore = create<ErrorGroupsState>((set, get) => ({
  // Initial state
  errorGroups: [],
  totalCount: 0,
  loading: false,
  currentPage: 1,
  pageSize: 25,

  // Get list of error groups
  getErrorGroups: async (params?: ErrorGroupListParams) => {
    set({ loading: true });
    try {
      const response = await errorGroupsService.getErrorGroups({
        page: get().currentPage,
        page_size: get().pageSize,
        ...params
      });

      set({
        errorGroups: response.results || [],
        totalCount: response.count || 0,
        loading: false
      });
    } catch (error: any) {
      console.error('Error fetching error groups:', error);
      showAlert('Error al cargar grupos de errores', {
        message: error.response?.data?.detail,
        status: 'error',
        variant: 'simple',
        close: true
      });
      set({
        errorGroups: [],
        totalCount: 0,
        loading: false
      });
    }
  },

  // Reprocess error groups
  reprocessErrorGroups: async (request: ReprocessRequest): Promise<boolean> => {
    try {
      const response = await errorGroupsService.reprocessErrors(request);

      showAlert('Reprocesamiento exitoso', {
        message: response.message,
        status: 'success',
        variant: 'simple',
        close: true
      });

      // Refresh error groups list after reprocessing
      await get().getErrorGroups();

      return true;
    } catch (error: any) {
      console.error('Error reprocessing error groups:', error);
      showAlert('Error al reprocesar', {
        message: error.response?.data?.detail || 'Error al reprocesar grupos',
        status: 'error',
        variant: 'simple',
        close: true
      });
      return false;
    }
  },

  // Set current page
  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  // Set page size
  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
  }
}));
